import { useApolloClient } from "@apollo/client";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useLocation, useNavigate } from "react-router";
import {
  useCreateFamily,
  useSearchApplicantDuplicatesKey,
  useSearchApplicantsKey,
  useSearchParentsKey,
} from "src/generated/hooks/form-service/queries";
import type { ParentSummary } from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useGlossary } from "src/hooks/useGlossary";
import { useOrganization } from "src/hooks/useOrganization";
import { useSchoolAdmin } from "src/hooks/useSchoolAdmin";
import { useUserPermissions } from "src/hooks/useUserPermissions";
import { canonicalPhoneNumber } from "src/services/format";
import * as Url from "src/services/url";
import {
  NewStudentEntry,
  useStudentEntries,
} from "./components/useStudentEntries";
import { GET_PARENTS } from "./graphql/queries";
import {
  INITIAL_PARENT_FORM,
  ParentProfileFormType,
  ParentProfileValidator,
} from "./schemas";

// Navigation state a create-new-parent action can transmit to prefill contact.
type NewParentNavState = {
  prefill?: { email?: string; phone?: string };
} | null;

/**
 * Holds all state, data fetching, and submit handling for the create-parent
 * scene. The parent is the single guardian in the createFamily payload; linked
 * children are students, and each gets a parent -> student relationship.
 */
export function useNewParent() {
  const [submitting, setSubmitting] = React.useState(false);
  const { glossary } = useGlossary();
  const toast = useAvelaToast();
  const navigate = useNavigate();
  const organization = useOrganization();
  const location = useLocation();
  const userPermissions = useUserPermissions();
  const { isSchoolAdmin } = useSchoolAdmin();

  const { mutateAsync: createFamily } = useCreateFamily();
  const students = useStudentEntries();
  const apolloClient = useApolloClient();
  const queryClient = useQueryClient();

  // When the user picks a parent from the possible-duplicates panel, we link the
  // students to that existing parent instead of creating a new one. The create
  // form is replaced by a read-only chip, and this parent is omitted from the
  // createFamily guardians array (referenced by id in the relationships only).
  const [selectedExisting, setSelectedExisting] =
    React.useState<ParentSummary | null>(null);

  const state = location.state as NewParentNavState;
  const initialValues: ParentProfileFormType = React.useMemo(
    () => ({
      ...INITIAL_PARENT_FORM,
      email_address: state?.prefill?.email ?? INITIAL_PARENT_FORM.email_address,
      phone_number: state?.prefill?.phone
        ? canonicalPhoneNumber(state.prefill.phone)
        : INITIAL_PARENT_FORM.phone_number,
    }),
    [state?.prefill?.email, state?.prefill?.phone]
  );

  const handleSubmit = async (form: ParentProfileFormType) => {
    organization.do(async (org) => {
      setSubmitting(true);
      try {
        // Existing parent (picked from duplicates): link only, no profile parse
        // and no guardian insert. New parent: parse the form and insert it.
        const parsed = selectedExisting
          ? null
          : ParentProfileValidator.parse(form);
        const parentId = selectedExisting?.id ?? crypto.randomUUID();
        // For an existing parent we do not have its address, so a new student that
        // opts into "use parent address" falls back to empty values.
        const parentAddress = {
          street_address: parsed?.street_address ?? null,
          street_address_line_2: parsed?.street_address_line_2 ?? null,
          city: parsed?.city ?? null,
          state: parsed?.state ?? null,
          zip_code: parsed?.zip_code ?? null,
        };

        // Only new students are created; existing ones are linked by id via the
        // relationships below. Parent is first_person, student is second_person.
        const studentPayload = students.entries
          .filter((entry): entry is NewStudentEntry => entry.kind === "new")
          .map((entry) => ({
            id: entry.id,
            first_name: entry.firstName,
            middle_name: entry.middleName,
            last_name: entry.lastName,
            birth_date: entry.birthDate,
            reference_id: entry.referenceId,
            ...(entry.useParentAddress
              ? parentAddress
              : {
                  street_address: entry.streetAddress,
                  street_address_line_2: entry.streetAddressLine2,
                  city: entry.city,
                  state: entry.state,
                  zip_code: entry.zipCode,
                }),
          }));
        const relationshipPayload = students.entries.map((entry) => ({
          first_person: parentId,
          second_person: entry.id,
        }));

        await createFamily({
          body: {
            data: {
              organizationId: org.id,
              students: studentPayload,
              // Omit the parent when linking to an existing one; it is referenced
              // by id in the relationships above.
              guardians: parsed
                ? [
                    {
                      id: parentId,
                      first_name: parsed.first_name,
                      middle_name: parsed.middle_name ?? null,
                      last_name: parsed.last_name,
                      // Email and phone are omitted by ParentProfileValidator, so
                      // read them from the raw form values.
                      email_address: form.email_address || null,
                      // Normalize any entered format to E.164 before transmitting.
                      phone_number: form.phone_number
                        ? canonicalPhoneNumber(form.phone_number) || null
                        : null,
                      reference_id: parsed.reference_id ?? null,
                      ...parentAddress,
                    },
                  ]
                : [],
              relationships: relationshipPayload,
            },
          },
        });

        // Refresh person-derived caches after the out-of-band family create.
        // refetchQueries updates any mounted (PreserveState-frozen) list observer;
        // evicting `person` + gc also clears lists that are not currently mounted,
        // so they refetch on next view instead of serving stale cache. A family
        // create can add both a guardian and a student, so this covers the parents
        // and students lists.
        await apolloClient.refetchQueries({
          include: [GET_PARENTS],
          updateCache: (cache) => {
            cache.evict({ fieldName: "person" });
            cache.gc();
          },
        });
        // The possible-duplicates panels and person pickers read react-query person
        // searches; invalidate them so the new records appear without a refresh.
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [useSearchApplicantsKey] }),
          queryClient.invalidateQueries({
            queryKey: [useSearchApplicantDuplicatesKey],
          }),
          queryClient.invalidateQueries({ queryKey: [useSearchParentsKey] }),
        ]);

        toast({
          title: selectedExisting
            ? glossary`Student added to parent/guardian`
            : glossary`Parent/guardian created successfully`,
          status: "success",
          isClosable: true,
        });
        // School admins (incl. elevated) may lack edit access to a parent with no
        // form at their school, so keep them on the list, not the edit page.
        navigate(
          selectedExisting && !isSchoolAdmin
            ? Url.OrgAdmin.Parents.edit(org, parentId)
            : Url.OrgAdmin.Parents.index(org)
        );
      } catch (error) {
        console.error(error);
        toast.error({ title: glossary`Error creating parent` });
      } finally {
        setSubmitting(false);
      }
    });
  };

  const canCreateUser = userPermissions.hasOne("user:create");
  const organizationId = React.useMemo(
    () => organization.map((org) => org.id).withDefault(""),
    [organization]
  );
  const cancelHref = organization
    .map((org) => Url.OrgAdmin.Parents.index(org))
    .withDefault("#");

  return {
    organization,
    organizationId,
    canCreateUser,
    initialValues,
    isSubmitting: submitting,
    onSubmit: handleSubmit,
    cancelHref,
    students,
    selectedExisting,
    onSelectExisting: setSelectedExisting,
    onClearExisting: () => setSelectedExisting(null),
  };
}
